import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSiteUsersDBData,
  SelectSiteUsersWeekData,
  setCurrentHeatMapDetail,
  SelectScreenshortURLs,
  updatePropertyVal,
  setFunnelViewDBData,
  SelectSiteUsersDBData,
  SelectSiteFunnelDBData,
  SelectSiteHeatmapUsersWeekLabels,
  setCurrentSelectedWeek,
  SelectCurrentSelectedWeek,
  setHeatmapActiveDateRange,
  SelectHeatmapActiveDateRange
} from "../../../HeatmapXUserSegmentSlice";
import { fetchGraphPersonaNudgeCoupon } from "../../../../Persona/components/NudgeBasedPerformance/nudgeCouponAPI";
import { FirebaseDataService } from "../../../../../services/firebase-data.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { nanoid } from "nanoid";
import { selectLogInUsername } from "../../../../../common/authenticateDashboard/authSlice";
import { Search } from "lucide-react";
import TempDropDown from "../../../../../components/Graphs/PersonaPredictionGraphs/TempDropDown";
import { getDataFromMongoDb, getDataInMongoDbByPath } from "../../../../../services/mongod.data";
import DateRangeFilter from "../../../../../components/Graphs/DateRangeFilter/DateRangeFilter";

const formatDateYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const TabularInsights = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const SiteUsersWeekLabels = useSelector(SelectSiteHeatmapUsersWeekLabels);
  const SiteUsersWeekData = useSelector(SelectSiteUsersWeekData);
  const screenshortURLs = useSelector(SelectScreenshortURLs);
  const SiteUsersDBData = useSelector(SelectSiteUsersDBData);
  // Always-fresh ref so callbacks never use stale closures
  const SiteUsersDBDataRef = React.useRef(SiteUsersDBData);
  useEffect(() => { SiteUsersDBDataRef.current = SiteUsersDBData; }, [SiteUsersDBData]);
  const siteFunnelDBData = useSelector(SelectSiteFunnelDBData);
  const persistedWeekLabel = useSelector(SelectCurrentSelectedWeek);
  const persistedDateRange = useSelector(SelectHeatmapActiveDateRange); // { start, end } or null

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWeekData, setSelectedWeekData] = useState([]);
  const [selectedWeekResult, setSelectedWeekResult] = useState([]);
  const [selectedWeekLabel, setSelectedWeekLabel] = useState("");
  const [processedData, setProcessedData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  // The active date range applied to aggregate data — restored from Redux on mount
  const [activeDateRange, setActiveDateRange] = useState(persistedDateRange);

  // Restore initial dates for DateRangeFilter UI (must be Date objects)
  const [initialStartDate] = useState(
    persistedDateRange?.start ? new Date(persistedDateRange.start) : null
  );
  const [initialEndDate] = useState(
    persistedDateRange?.end ? new Date(persistedDateRange.end) : null
  );

  // new shape: { tree: { year: { month: [dates...] } }, flat: ['YYYY-MM-DD', ...], latest: 'YYYY-MM-DD' }
  const [availableDates, setAvailableDates] = useState([]);

  const itemsPerPage = 10;
  const [sortConfig, setSortConfig] = useState({ column: "clicks", order: "desc" });
  const sessionUser = useSelector(selectLogInUsername);
  const user = sessionUser?.split("@")[0];
  const firebaseDataService = new FirebaseDataService();
  // ---------- helper: compute insight ----------
  const GetHeatmapURLInsight = (data) => {
    let insightResult = {};

    Object.entries(data || {}).forEach(([url, urlData]) => {
      let result = {
        clicks: 0,
        moves: 0,
        scrollDepth: 0,
        scrollPerc: {}
      };

      let { blocks, scrollDepth_perc } = urlData || {};

      if (scrollDepth_perc === undefined) {
        result["scrollDepth"] = 0;
      } else {
        let maxScrollFromPerc = 0;
        Object.entries(scrollDepth_perc).forEach(([range, count]) => {
          if (count > 0) {
            const parts = range.split('_');
            const upperBound = parseInt(parts[1], 10);
            maxScrollFromPerc = Math.max(maxScrollFromPerc, upperBound);
          }
        });
        result["scrollDepth"] = maxScrollFromPerc;
        result["scrollPerc"] = { ...scrollDepth_perc } || {};
      }

      if (blocks) {
        Object.entries(blocks).forEach(([xy, blockData]) => {
          // Skip "all" userType if individual types exist (to avoid double-counting aggregates)
          const userTypeKeys = Object.keys(blockData || {});
          const userTypesToCount = userTypeKeys.filter(k => k !== "all").length > 0
            ? userTypeKeys.filter(k => k !== "all")
            : userTypeKeys;

          userTypesToCount.forEach((userType) => {
            const userTypeData = blockData[userType];
            // Skip "all" userTraffic if individual sources exist
            const userTrafficKeys = Object.keys(userTypeData || {});
            const userTrafficToCount = userTrafficKeys.filter(k => k !== "all").length > 0
              ? userTrafficKeys.filter(k => k !== "all")
              : userTrafficKeys;

            userTrafficToCount.forEach((userTraffic) => {
              const userTrafficData = userTypeData[userTraffic];
              Object.entries(userTrafficData || {}).forEach(([userDevice, userData]) => {
                let { clicks, moves } = userData || {};
                result.clicks += clicks || 0;
                result.moves += moves || 0;
              });
            });
          });
        });
      }
      let newURL = decodeURIComponent(url);
      insightResult[newURL] = result;
    });

    return insightResult;
  };

  const aggregateInsightsForRange = useCallback((startDateStr, endDateStr) => {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    end.setHours(23, 59, 59, 999);
    const aggregatedData = {};
    Object.entries(SiteUsersDBData).forEach(([label, weekData]) => {
      const labelDate = new Date(label);
      if (labelDate >= start && labelDate <= end) {
        const insight = GetHeatmapURLInsight(weekData);
        Object.entries(insight).forEach(([url, data]) => {
          if (!aggregatedData[url]) {
            aggregatedData[url] = { clicks: 0, moves: 0, scrollDepth: 0, scrollPerc: {} };
          }
          aggregatedData[url].clicks += data.clicks;
          aggregatedData[url].moves += data.moves;
          aggregatedData[url].scrollDepth = Math.max(aggregatedData[url].scrollDepth, data.scrollDepth);
          Object.entries(data.scrollPerc).forEach(([range, count]) => {
            aggregatedData[url].scrollPerc[range] = (aggregatedData[url].scrollPerc[range] || 0) + count;
          });
        });
      }
    });
    return aggregatedData;
  }, [SiteUsersDBData]); // Cannot put GetHeatmapURLInsight in deps because it's redefined on every render without useCallback

  // Aggregate raw block data for a single URL across the active date range.
  // Uses SiteUsersDBDataRef so it always reads the latest Redux data.
  const aggregateRawDataForURL = useCallback((url, startDateStr, endDateStr) => {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    end.setHours(23, 59, 59, 999);

    const freshData = SiteUsersDBDataRef.current;
    console.log("[aggregateRawDataForURL] url:", url, "range:", startDateStr, "->", endDateStr);
    console.log("[aggregateRawDataForURL] keys in SiteUsersDBData:", Object.keys(freshData));

    const merged = { blocks: {}, scrollDepth_perc: {} };
    Object.entries(freshData).forEach(([label, weekData]) => {
      const labelDate = new Date(label);
      if (labelDate < start || labelDate > end) return;
      // Try exact match first, then decoded match
      const encodedUrl = weekData[url] ? url
        : Object.keys(weekData).find(k => decodeURIComponent(k) === url || k === url) || url;
      const urlData = weekData[encodedUrl];
      if (!urlData) {
        console.log("[aggregateRawDataForURL] no data for url", url, "on date", label);
        return;
      }
      console.log("[aggregateRawDataForURL] merging date", label, "blocks count:", Object.keys(urlData.blocks || {}).length);

      // Merge blocks
      Object.entries(urlData.blocks || {}).forEach(([xy, xyData]) => {
        if (!merged.blocks[xy]) merged.blocks[xy] = {};
        Object.entries(xyData).forEach(([userType, utData]) => {
          if (!merged.blocks[xy][userType]) merged.blocks[xy][userType] = {};
          Object.entries(utData).forEach(([userSource, usData]) => {
            if (!merged.blocks[xy][userType][userSource]) merged.blocks[xy][userType][userSource] = {};
            Object.entries(usData).forEach(([device, devData]) => {
              if (!merged.blocks[xy][userType][userSource][device]) {
                merged.blocks[xy][userType][userSource][device] = { clicks: 0, moves: 0 };
              }
              merged.blocks[xy][userType][userSource][device].clicks += devData.clicks || 0;
              merged.blocks[xy][userType][userSource][device].moves += devData.moves || 0;
            });
          });
        });
      });

      // Merge scrollDepth_perc
      Object.entries(urlData.scrollDepth_perc || {}).forEach(([range, count]) => {
        merged.scrollDepth_perc[range] = (merged.scrollDepth_perc[range] || 0) + (count || 0);
      });
    });
    console.log("[aggregateRawDataForURL] merged block count:", Object.keys(merged.blocks).length);
    return merged;
  }, []);

  const fetchMissingDatesForRange = useCallback(async (datesToFetch) => {
    setLoading(true);
    let newHeatmapData = { ...SiteUsersDBData };
    let newJourneyData = { ...siteFunnelDBData };
    
    for (const dateLabel of datesToFetch) {
      const [yy, mm, dd] = dateLabel.split("-");
      try {
        const data = await firebaseDataService.getDataForDate(user, yy, mm, dd);
        if (data) {
          newHeatmapData[dateLabel] = data.heatmap ?? {};
          newJourneyData[dateLabel] = data.journey ?? {};
        }
      } catch (error) {
        console.error('Error fetching data for date:', dateLabel, error);
      }
    }
    
    dispatch(setSiteUsersDBData(newHeatmapData));
    dispatch(setFunnelViewDBData(newJourneyData));
    setLoading(false);
  }, [SiteUsersDBData, siteFunnelDBData, user, dispatch]);

  const handleDateRangeChange = useCallback(async ({ startDate, endDate, filteredIndices }) => {
    if (startDate && endDate && filteredIndices) {
      const datesToFetch = filteredIndices
        .map(i => availableDates[i])
        .filter(dateLabel => !SiteUsersDBData[dateLabel]);

      if (datesToFetch.length > 0) {
        await fetchMissingDatesForRange(datesToFetch);
      }

      const newRange = {
        start: formatDateYMD(startDate),
        end: formatDateYMD(endDate),
      };
      setActiveDateRange(newRange);
      dispatch(setHeatmapActiveDateRange(newRange)); // persist in Redux
    } else {
      setActiveDateRange(null);
      dispatch(setHeatmapActiveDateRange(null)); // clear from Redux
    }
  }, [availableDates, SiteUsersDBData, fetchMissingDatesForRange, dispatch]);

  useEffect(() => {
    console.log("selectedWeekLabel changed:", selectedWeekLabel, SiteUsersDBData);
    if (!selectedWeekLabel) return;
    // If a date range is active, use aggregated data instead of single-date data
    if (activeDateRange) return;
    const stored = SiteUsersDBData?.[selectedWeekLabel];
    if (stored) {
      setSelectedWeekData(stored);
      setSelectedWeekResult(GetHeatmapURLInsight(stored));
    }
    // else: when we lazy-fetch the date, fetchDataForDate will setSelectedWeekData/result directly
  }, [selectedWeekLabel, SiteUsersDBData, activeDateRange]);

  // Re-compute when date range changes
  useEffect(() => {
    if (activeDateRange) {
      const aggregated = aggregateInsightsForRange(activeDateRange.start, activeDateRange.end);
      setSelectedWeekResult(aggregated);
    } else if (selectedWeekLabel) {
      // Back to single-date (current week) view
      const stored = SiteUsersDBData?.[selectedWeekLabel];
      if (stored) {
        setSelectedWeekData(stored);
        setSelectedWeekResult(GetHeatmapURLInsight(stored));
      }
    }
  }, [activeDateRange, aggregateInsightsForRange, selectedWeekLabel, SiteUsersDBData]);

  useEffect(() => {
    if (SiteUsersWeekLabels.length) {
      setAvailableDates(SiteUsersWeekLabels);
      // Restore persisted date if available, otherwise default to first date
      const labelToUse = persistedWeekLabel && SiteUsersWeekLabels.includes(persistedWeekLabel)
        ? persistedWeekLabel
        : SiteUsersWeekLabels[0];
      const labelIndex = SiteUsersWeekLabels.indexOf(labelToUse);
      setSelectedWeekData(SiteUsersWeekData[labelIndex]);
      let insgihtResult = GetHeatmapURLInsight(SiteUsersWeekData[labelIndex]);
      console.log("current Week HeatmapInsights", insgihtResult);
      setSelectedWeekResult(insgihtResult);
      setSelectedWeekLabel(labelToUse);
      return;
    }
  }, [SiteUsersWeekLabels]);


  // useEffect(() => {
  //   const initializeData = async () => {
  //     try {
  //       // Get available dates tree
  //       const dateTree = await firebaseDataService.getAvailableDateTree(user);
  //       setAvailableDates(dateTree);
  //       dispatch(setSiteUserWeekLabels(dateTree));
  //       console.log("all Available dates fetches->",dateTree)

  //       // If we have a latest date, fetch its data
  //       if (dateTree) {
  //         const [year, month, date] = dateTree[0].split('-');
  //         const data = await firebaseDataService.getDataForDate(user, year, month, date);

  //         console.log("first time data fetched ->",data);
          
  //         setSelectedWeekLabel(data.dateLabel);
  //         setSelectedWeekData(data.heatmap);
  //         setSelectedWeekResult(GetHeatmapURLInsight(data.heatmap));
          
  //         dispatch(setSiteUsersDBData({
  //           ...SiteUsersDBData,
  //           [dateTree[0]]: data.heatmap ?? {}
  //         }));
  //         dispatch(setFunnelViewDBData({
  //           ...siteFunnelDBData,
  //           [dateTree[0]] : data.journey ?? {}
  //         }));
  //       }

  //       // fetch last 4 weeks data in
  //       // setTimeout(async () => {
  //       //   if(dateTree.length < 2) return;
  //       //   for (let i = 1; i < Math.min(4, dateTree.length); i++) {
  //       //     console.log("fetching data for date:", dateTree[i]);
  //       //     const [year, month, date] = dateTree[i].split('-');
  //       //     const data = await firebaseDataService.getDataForDate(user, year, month, date);
  //       //     dispatch(setSiteUsersDBData({
  //       //       ...SiteUsersDBData,
  //       //       [dateTree[i]]: data.heatmap ?? {}
  //       //     }));
  //       //     dispatch(setFunnelViewDBData({
  //       //       ...siteFunnelDBData,
  //       //       [dateTree[i]] : data.journey ?? {}
  //       //     }));
  //       //   }
  //       // }, 5000); // stagger by 5 second to avoid overwhelming

  //     } catch (error) {
  //       console.error('Error initializing data:', error);
  //     }
  //   };

  //   if (!SiteUsersWeekLabels.length) {
  //     console.log("SiteUsersWeekLabels changed, re-initializing if empty:", SiteUsersWeekLabels);
  //     initializeData();
  //   }
  // }, []);

// Update fetchDataForDate function:
const fetchDataForDate = async (year, month, date) => {
  console.log(`Fetching data for date: ${year}-${month}-${date}`);
  const newLabel = `${year}-${month}-${date}`;
  setSelectedWeekLabel(newLabel);
  dispatch(setCurrentSelectedWeek(newLabel));
  try {
    const mm = String(month).padStart(2, '0');
    const dd = String(date).padStart(2, '0');
    const dateKey = `${year}-${mm}-${dd}`;
    
    // FIRST CHECK REDUX STORE
    const cachedData = SiteUsersDBData?.[dateKey];
    
    if (cachedData) {
      console.log(`Using Redux cached data for ${dateKey}`);
      setSelectedWeekData(cachedData);
      setSelectedWeekResult(GetHeatmapURLInsight(cachedData));
      return; // No Firebase call needed!
    }

    const data = await firebaseDataService.getDataForDate(user, year, month, date);

    setSelectedWeekData(data.heatmap);
    setSelectedWeekResult(GetHeatmapURLInsight(data.heatmap));
    dispatch(setSiteUsersDBData({
      ...SiteUsersDBData,
      [dateKey]: data.heatmap ?? {}
    }));
    dispatch(setFunnelViewDBData({
      ...siteFunnelDBData,
      [dateKey] : data.journey ?? {}
    }));

  } catch (error) {
    console.error('Error fetching data for date:', error);
  }
};

  const handleDetailURLResult = async (url, data) => {
    const heatmapId = nanoid();
    let screenshotURL = await fetchScreenshotURL(url, heatmapId);
    if (!screenshotURL) {
      console.log("issue with screenshotURL", screenshotURL);
      return;
    }

    let outData;
    if (activeDateRange) {
      outData = aggregateRawDataForURL(url, activeDateRange.start, activeDateRange.end);
    } else {
      outData = selectedWeekData[url];
    }

    dispatch(
      setCurrentHeatMapDetail({
        targetRoute: url,
        heatmapId,
        data: outData,
        screenshotURL,
        weekLabel: selectedWeekLabel,
      })
    );
    navigate(`/heatmap/view/${heatmapId}`);
  };

  const fetchScreenshotURL = async (url, heatmapId, update = "false") => {
    setLoading(true);
    if (update === "false") {
      const matchedData = screenshortURLs.find((heatmapItem) => heatmapItem.originalURL === url);
      if (matchedData) {
        setLoading(false);
        return matchedData;
      } else {
        console.log("No matching URL found, fetching new screenshot.");
      }
    }

    try {
      const postData = { website: url, urlId: heatmapId, dbProd: user };
      const api_link = "https://www.api.causalfunnels.com/screenshot";
      const response = await fetch(api_link, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(postData),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch new image URL: ${response.statusText}`);
      }
      const responseData = await response.json();
      dispatch(updatePropertyVal({ key: "screenshortURLs", val: [...(screenshortURLs || []), responseData.screenshots] }));
      return responseData.screenshots;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUrlResult = async (url, data) => {
    const heatmapId = nanoid();
    let screenshotURL = await fetchScreenshotURL(url, heatmapId, "true");
    if (!screenshotURL) {
      console.log("issue with screenshotURL, trying fallback...", screenshotURL);
      const matchedData = screenshortURLs.find((heatmapItem) => heatmapItem.originalURL === url);
      if (matchedData) {
        screenshotURL = matchedData;
      } else {
        alert("Could not update screenshot. External API failed and no cached version exists.");
        return;
      }
    }

    let outData;
    if (activeDateRange) {
      outData = aggregateRawDataForURL(url, activeDateRange.start, activeDateRange.end);
    } else {
      outData = selectedWeekData[url];
    }

    dispatch(
      setCurrentHeatMapDetail({
        targetRoute: url,
        heatmapId,
        data: outData,
        screenshotURL,
        weekLabel: selectedWeekLabel,
      })
    );
    navigate(`/heatmap/view/${heatmapId}`);
  };

  // ---------- table + paging + sorting logic (kept as you had it) ----------
  const totalPages = Math.ceil(Object.entries(selectedWeekResult).length / itemsPerPage);

  const handlePageClick = (pageNumber) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(pageNumber));
      return next;
    });
  };

  const handleSort = (column) => {
    setSortConfig((prevConfig) => {
      const newOrder = prevConfig.column === column && prevConfig.order === "asc" ? "desc" : "asc";
      return { column, order: newOrder };
    });
  };

  const sortedData = React.useMemo(() => {
    const entries = Object.entries(selectedWeekResult);
    const filtered = searchTerm
      ? entries.filter(([url]) => url.toLowerCase().includes(searchTerm.toLowerCase()))
      : entries;

    const sorted = filtered.sort(([urlA, dataA], [urlB, dataB]) => {
      const { column, order } = sortConfig;
      const valueA = dataA[column];
      const valueB = dataB[column];

      if (typeof valueA === "number" && typeof valueB === "number") {
        return order === "asc" ? valueA - valueB : valueB - valueA;
      } else {
        return order === "asc"
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });

    return sorted;
  }, [selectedWeekResult, sortConfig, searchTerm]);

  const paginatedData = React.useMemo(() => {
    return sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [sortedData, currentPage]);

  useEffect(() => {
    if (Object.keys(selectedWeekResult).length > 0) {
      setSortConfig({ column: "clicks", order: "desc" });
    }
  }, [selectedWeekResult]);

  // ---------- render UI ----------
  return (
    <div className="bg-white rounded-lg p-4 relative">
      {loading && <LoadingHeatMapInsight setLoading={setLoading} />}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <span className="bg-indigo-100 text-indigo-800 text-md font-medium me-2 px-2.5 py-0.5 rounded dark:bg-indigo-900 dark:text-indigo-300 whitespace-nowrap">
          Total URLs: {Object.entries(selectedWeekResult).length}
        </span>

        <div className="relative flex-1 w-full max-w-2xl min-w-[200px]">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-700">
            <Search className="h-5 w-5" />
          </span>
          <input
            type="text"
            placeholder="Search"
            className="w-full rounded-lg border border-gray-900 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative z-10">
          <DateRangeFilter
            weeks={availableDates}
            onFilterChange={handleDateRangeChange}
            initialStartDate={initialStartDate}
            initialEndDate={initialEndDate}
          />
        </div>
      </div>
      
      {/* Table (unchanged) */}
      <div className="overflow-x-auto">
        <table className="w-full divide-y divide-gray-200 border-2">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page URL</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("clicks")}>
                Total Clicks {sortConfig.column === "clicks" && (sortConfig.order === "asc" ? "▲" : "▼")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("moves")}>
                Avg. Cursor Moves {sortConfig.column === "moves" && (sortConfig.order === "asc" ? "▲" : "▼")}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort("scrollDepth")}>
                Scroll Depth (%) {sortConfig.column === "scrollDepth" && (sortConfig.order === "asc" ? "▲" : "▼")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detailed View</th>
              <th className="px-10 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData?.map(([url, result]) => (
              <tr key={url} className="hover:bg-gray-50 transition-colors duration-200">
                <td className="px-6 py-4 break-words max-w-xs text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <a href={url} title={url} target="_blank" rel="noopener noreferrer">{url}</a>
                </td>
                <td className="px-6 py-4 text-right">{result.clicks}</td>
                <td className="px-6 py-4 text-right">{result.moves}</td>
                <td className="px-6 py-4 text-right">{Number(result.scrollDepth).toFixed(2)}%</td>
                <td className="px-6 py-4">
                  <button onClick={() => handleDetailURLResult(url, result)} className="text-indigo-500 border border-indigo-600 py-1 px-1 rounded-md text-sm hover:bg-indigo-500 hover:text-white transition">
                    View Details
                  </button>
                </td>
                <td className="px-2 py-4">
                  <button onClick={() => handleUpdateUrlResult(url, result)} className="text-indigo-500 border border-indigo-600 py-1 px-1 rounded-md text-sm hover:bg-indigo-500 hover:text-white transition ml-2">
                    Update Screenshot
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-center">
        <nav className="flex items-center space-x-2">
          {/* First Page */}
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageClick(1)}
            className={`px-2 py-1 border rounded ${
              currentPage === 1
                ? "text-gray-400 border-gray-200"
                : "text-blue-600 border-blue-400 hover:bg-blue-100"
            }`}
          >
            &laquo;
          </button>

          {/* Previous Page */}
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageClick(currentPage - 1)}
            className={`px-2 py-1 border rounded ${
              currentPage === 1
                ? "text-gray-400 border-gray-200"
                : "text-blue-600 border-blue-400 hover:bg-blue-100"
            }`}
          >
            &lt;
          </button>

          {/* Dynamic Page Buttons (limited to 15) */}
          {(() => {
            const visiblePages = 15;
            let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
            let endPage = startPage + visiblePages - 1;

            if (endPage > totalPages) {
              endPage = totalPages;
              startPage = Math.max(1, endPage - visiblePages + 1);
            }

            const pages = [];
            if (startPage > 1) {
              pages.push(
                <button
                  key="start-ellipsis"
                  disabled
                  className="px-2 py-1 text-gray-500"
                >
                  ...
                </button>
              );
            }

            for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
              pages.push(
                <button
                  key={pageNumber}
                  onClick={() => handlePageClick(pageNumber)}
                  className={`px-3 py-1 border rounded ${
                    pageNumber === currentPage
                      ? "bg-blue-500 text-white border-blue-500"
                      : "text-blue-600 border-blue-400 hover:bg-blue-100"
                  }`}
                >
                  {pageNumber}
                </button>
              );
            }

            if (endPage < totalPages) {
              pages.push(
                <button
                  key="end-ellipsis"
                  disabled
                  className="px-2 py-1 text-gray-500"
                >
                  ...
                </button>
              );
            }

            return pages;
          })()}

          {/* Next Page */}
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageClick(currentPage + 1)}
            className={`px-2 py-1 border rounded ${
              currentPage === totalPages
                ? "text-gray-400 border-gray-200"
                : "text-blue-600 border-blue-400 hover:bg-blue-100"
            }`}
          >
            &gt;
          </button>

          {/* Last Page */}
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageClick(totalPages)}
            className={`px-2 py-1 border rounded ${
              currentPage === totalPages
                ? "text-gray-400 border-gray-200"
                : "text-blue-600 border-blue-400 hover:bg-blue-100"
            }`}
          >
            &raquo;
          </button>
        </nav>
      </div>
    </div>
  );
};

export default TabularInsights;

export const LoadingHeatMapInsight = ({ setLoading }) => {
  return (
    <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-start pt-20 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center relative w-80">
        <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full slow-spin"></div>
        <p className="mt-4 text-lg font-semibold text-gray-700 text-center">
          Preparing Your <span className="text-blue-500">Heatmap Insights</span>...
        </p>
        <p className="text-sm text-gray-500 mt-2">Fetching website preview and analyzing user interactions.</p>
      </div>
    </div>
  );
};